import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BlockIcon from '@mui/icons-material/Block';
import { useTranslation } from 'react-i18next';
import { ecommerceApi } from '../../../config/apiClient';

interface ReturnRecord {
  id: string;
  orderId: string;
  memberId: string;
  returnNumber: string;
  reason?: string;
  reasonDetail?: string;
  refundAmount: number;
  refundShipping?: boolean;
  status: string;
  adminNote?: string;
  createdAt: string;
  refundedAt?: string;
  returnInvoiceId?: string | null;
  refundMode?: string | null;
  expenseVoucherStatus?: string | null;
  expenseVoucherNo?: string | null;
  expenseVoucherDate?: string | null;
  expenseVoucherPdfUrl?: string | null;
  // Bağlı sipariş bilgisi (API'de zenginleştirilir)
  orderNumber?: string | null;
  orderCurrency?: string | null;
  appliedExchangeRate?: number | null;
  customerName?: string | null;
}

interface ReturnsManagementProps {
  companyId: string | null;
}

const STATUS_COLORS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  Requested: 'info',
  Approved: 'info',
  PickupScheduled: 'info',
  PickedUp: 'info',
  Received: 'warning',
  Inspected: 'warning',
  Refunded: 'success',
  Rejected: 'error'
};

const ReturnsManagement: React.FC<ReturnsManagementProps> = ({ companyId }) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [returns, setReturns] = useState<ReturnRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [voucherFilter, setVoucherFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // İadeyi işle (ters kayıt) diyaloğu
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundTarget, setRefundTarget] = useState<ReturnRecord | null>(null);
  const [refundMode, setRefundMode] = useState('balance');
  const [customerIssuesInvoice, setCustomerIssuesInvoice] = useState(false);
  const [refundProcessing, setRefundProcessing] = useState(false);

  // Gider pusulası diyaloğu
  const [voucherDialogOpen, setVoucherDialogOpen] = useState(false);
  const [voucherTarget, setVoucherTarget] = useState<ReturnRecord | null>(null);
  const [voucherNo, setVoucherNo] = useState('');
  const [voucherDate, setVoucherDate] = useState('');
  const [voucherPdfUrl, setVoucherPdfUrl] = useState('');
  const [voucherSaving, setVoucherSaving] = useState(false);

  // İade reddi diyaloğu
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<ReturnRecord | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSaving, setRejectSaving] = useState(false);

  const loadReturns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ecommerceApi.get('/api/ecommerce/returns');
      const data = res.data?.data || [];
      setReturns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('[ReturnsManagement] İadeler yüklenemedi:', err);
      setSnackbar({ open: true, message: tt('İade listesi yüklenemedi.', 'Failed to load returns.'), severity: 'error' });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId) loadReturns();
  }, [companyId, loadReturns]);

  const formatDate = (value?: string | null) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString(localeCode, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  // Tutarlar SİPARİŞİN para birimiyle gösterilir (null = TL); TRY dışıysa TL karşılığı eklenir
  const formatMoney = (amount: number, currency?: string | null) =>
    new Intl.NumberFormat(localeCode, { style: 'currency', currency: (currency || 'TRY').toUpperCase() }).format(amount || 0);

  const tlEquivalent = (r: ReturnRecord): number | null => {
    const code = (r.orderCurrency || 'TRY').toUpperCase();
    if (code === 'TRY' || !r.appliedExchangeRate || r.appliedExchangeRate <= 0) return null;
    return Math.round((r.refundAmount || 0) * r.appliedExchangeRate * 100) / 100;
  };

  const statusLabel = (status: string) => {
    const map: Record<string, [string, string]> = {
      Requested: ['Talep Edildi', 'Requested'],
      Approved: ['Onaylandı', 'Approved'],
      Rejected: ['Reddedildi', 'Rejected'],
      PickupScheduled: ['Kargo Planlandı', 'Pickup Scheduled'],
      PickedUp: ['Kargoya Verildi', 'Picked Up'],
      Received: ['Teslim Alındı', 'Received'],
      Inspected: ['İncelendi', 'Inspected'],
      Refunded: ['İade Edildi', 'Refunded']
    };
    const entry = map[status];
    return entry ? tt(entry[0], entry[1]) : status;
  };

  const renderVoucherChip = (r: ReturnRecord) => {
    if (!r.expenseVoucherStatus) {
      return <Chip size="small" variant="outlined" label={tt('Gerekmiyor', 'N/A')} />;
    }
    if (r.expenseVoucherStatus === 'issued') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Chip size="small" color="success" label={tt('Düzenlendi', 'Issued')} />
          {r.expenseVoucherNo && (
            <Typography variant="caption" color="textSecondary">
              {r.expenseVoucherNo} · {formatDate(r.expenseVoucherDate)}
            </Typography>
          )}
        </Box>
      );
    }
    return <Chip size="small" color="warning" label={tt('Bekliyor', 'Pending')} />;
  };

  // ===== İadeyi işle =====
  const openRefundDialog = (r: ReturnRecord) => {
    setRefundTarget(r);
    setRefundMode(r.refundMode || 'balance');
    setCustomerIssuesInvoice(false);
    setRefundDialogOpen(true);
  };

  const handleProcessRefund = async () => {
    if (!refundTarget) return;
    setRefundProcessing(true);
    try {
      const res = await ecommerceApi.post('/api/ecommerce/returns/process-refund', {
        returnId: refundTarget.id,
        refundMode,
        customerIssuesReturnInvoice: customerIssuesInvoice
      });
      if (res.data?.success) {
        setSnackbar({ open: true, message: res.data.message || tt('İade işlendi.', 'Refund processed.'), severity: 'success' });
        setRefundDialogOpen(false);
        loadReturns();
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      console.error('[ReturnsManagement] İade işleme hatası:', err);
      // Backend 500 yanıtında asıl sebep "error" alanında döner; teşhis için onu da göster.
      const data = err?.response?.data;
      setSnackbar({
        open: true,
        message: data?.error || data?.message || tt('İade işlenemedi.', 'Failed to process refund.'),
        severity: 'error'
      });
    } finally {
      setRefundProcessing(false);
    }
  };

  // ===== İade reddi =====
  const openRejectDialog = (r: ReturnRecord) => {
    setRejectTarget(r);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectReturn = async () => {
    if (!rejectTarget || !rejectReason.trim()) return;
    setRejectSaving(true);
    try {
      const res = await ecommerceApi.post('/api/ecommerce/returns/update-status', {
        returnId: rejectTarget.id,
        status: 'Rejected',
        rejectionReason: rejectReason.trim()
      });
      if (res.data?.success) {
        setSnackbar({ open: true, message: tt('İade talebi reddedildi.', 'Return request rejected.'), severity: 'success' });
        setRejectDialogOpen(false);
        loadReturns();
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      console.error('[ReturnsManagement] İade reddi hatası:', err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || tt('İade reddedilemedi.', 'Failed to reject return.'),
        severity: 'error'
      });
    } finally {
      setRejectSaving(false);
    }
  };

  // ===== Gider pusulası =====
  const openVoucherDialog = (r: ReturnRecord) => {
    setVoucherTarget(r);
    setVoucherNo(r.expenseVoucherNo || '');
    setVoucherDate(r.expenseVoucherDate ? r.expenseVoucherDate.substring(0, 10) : '');
    setVoucherPdfUrl(r.expenseVoucherPdfUrl || '');
    setVoucherDialogOpen(true);
  };

  const handleSaveVoucher = async () => {
    if (!voucherTarget || !voucherNo.trim()) return;
    setVoucherSaving(true);
    try {
      const res = await ecommerceApi.post('/api/ecommerce/returns/expense-voucher', {
        returnId: voucherTarget.id,
        documentNo: voucherNo.trim(),
        documentDate: voucherDate ? new Date(voucherDate).toISOString() : null,
        pdfUrl: voucherPdfUrl.trim() || null
      });
      if (res.data?.success) {
        setSnackbar({ open: true, message: tt('Gider pusulası kaydedildi.', 'Expense voucher saved.'), severity: 'success' });
        setVoucherDialogOpen(false);
        loadReturns();
      } else {
        throw new Error(res.data?.message);
      }
    } catch (err: any) {
      console.error('[ReturnsManagement] Gider pusulası hatası:', err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || tt('Gider pusulası kaydedilemedi.', 'Failed to save expense voucher.'),
        severity: 'error'
      });
    } finally {
      setVoucherSaving(false);
    }
  };

  // ===== Filtreleme =====
  const filteredReturns = returns.filter(r => {
    const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchesVoucher =
      voucherFilter === 'all' ||
      (voucherFilter === 'pending' && r.expenseVoucherStatus === 'pending') ||
      (voucherFilter === 'issued' && r.expenseVoucherStatus === 'issued');
    const term = searchTerm.trim().toLowerCase();
    const matchesSearch = !term
      || r.returnNumber?.toLowerCase().includes(term)
      || r.orderId?.toLowerCase().includes(term)
      || r.orderNumber?.toLowerCase().includes(term)
      || r.customerName?.toLowerCase().includes(term)
      || r.reason?.toLowerCase().includes(term);
    return matchesStatus && matchesVoucher && matchesSearch;
  });

  const paginatedReturns = filteredReturns.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const pendingVoucherCount = returns.filter(r => r.expenseVoucherStatus === 'pending').length;

  if (!companyId) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Alert severity="warning">{tt('İade yönetimi için şirket bilgisi gereklidir.', 'Company info is required for returns management.')}</Alert>
      </Paper>
    );
  }

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <AssignmentReturnIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight="bold">
            {tt('İade Yönetimi', 'Returns Management')}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title={tt('Yenile', 'Refresh')}>
            <IconButton onClick={loadReturns}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {pendingVoucherCount > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<DescriptionIcon />}>
            {tt(
              `${pendingVoucherCount} iadenin gider pusulası bekliyor. Belge düzenlenip numarası girilmelidir (e-Arşiv/B2C iadesi).`,
              `${pendingVoucherCount} return(s) awaiting expense voucher. Issue the document and record its number (e-Archive/B2C return).`
            )}
          </Alert>
        )}

        {/* Filtreler */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={tt('İade no / sipariş no / neden ara...', 'Search return no / order no / reason...')}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('Durum', 'Status')}</InputLabel>
                  <Select
                    value={statusFilter}
                    label={tt('Durum', 'Status')}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="all">{tt('Tümü', 'All')}</MenuItem>
                    {['Requested', 'Approved', 'Received', 'Inspected', 'Refunded', 'Rejected'].map(s => (
                      <MenuItem key={s} value={s}>{statusLabel(s)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>{tt('Gider Pusulası', 'Expense Voucher')}</InputLabel>
                  <Select
                    value={voucherFilter}
                    label={tt('Gider Pusulası', 'Expense Voucher')}
                    onChange={(e) => { setVoucherFilter(e.target.value); setPage(0); }}
                  >
                    <MenuItem value="all">{tt('Tümü', 'All')}</MenuItem>
                    <MenuItem value="pending">{tt('Bekliyor', 'Pending')}</MenuItem>
                    <MenuItem value="issued">{tt('Düzenlendi', 'Issued')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Liste */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredReturns.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="h6" color="textSecondary">
              {tt('İade kaydı bulunamadı.', 'No return records found.')}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tt('İade No', 'Return No')}</TableCell>
                    <TableCell>{tt('Sipariş', 'Order')}</TableCell>
                    <TableCell>{tt('Tarih', 'Date')}</TableCell>
                    <TableCell>{tt('Neden', 'Reason')}</TableCell>
                    <TableCell align="right">{tt('Tutar', 'Amount')}</TableCell>
                    <TableCell>{tt('Durum', 'Status')}</TableCell>
                    <TableCell>{tt('Ters Kayıt / İade Modu', 'Reversal / Refund Mode')}</TableCell>
                    <TableCell>{tt('Gider Pusulası', 'Expense Voucher')}</TableCell>
                    <TableCell align="center">{tt('İşlemler', 'Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedReturns.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{r.returnNumber}</Typography>
                      </TableCell>
                      <TableCell>
                        {r.orderNumber ? (
                          <Typography variant="body2">#{r.orderNumber}</Typography>
                        ) : (
                          <Typography variant="caption">{r.orderId?.substring(0, 8)}...</Typography>
                        )}
                        {r.customerName && (
                          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                            {r.customerName}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(r.createdAt)}</TableCell>
                      <TableCell>
                        <Tooltip title={r.reasonDetail || ''}>
                          <Typography variant="body2">{r.reason || '-'}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(r.refundAmount, r.orderCurrency)}
                        {(() => {
                          const tl = tlEquivalent(r);
                          return tl != null ? (
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                              ≈ {formatMoney(tl, 'TRY')}
                            </Typography>
                          ) : null;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={statusLabel(r.status)} color={STATUS_COLORS[r.status] || 'default'} />
                      </TableCell>
                      <TableCell>
                        {r.returnInvoiceId || r.refundMode ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {r.returnInvoiceId && (
                              <Chip size="small" variant="outlined" color="success" icon={<ReceiptLongIcon />} label={tt('İade faturası kesildi', 'Return invoice issued')} />
                            )}
                            {r.refundMode && (
                              <Typography variant="caption" color="textSecondary">
                                {r.refundMode === 'provider'
                                  ? tt('Para iadesi: sağlayıcı', 'Refund: provider')
                                  : tt('Para iadesi: cari bakiye', 'Refund: account balance')}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Chip size="small" variant="outlined" label={tt('İşlenmedi', 'Not processed')} />
                        )}
                      </TableCell>
                      <TableCell>{renderVoucherChip(r)}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                          {r.status !== 'Rejected' && !r.returnInvoiceId && !r.refundMode && (
                            <Tooltip title={tt('İadeyi işle (ters kayıt + para iadesi)', 'Process refund (reversal + refund)')}>
                              <IconButton size="small" color="primary" onClick={() => openRefundDialog(r)}>
                                <PlayCircleOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.status !== 'Rejected' && r.status !== 'Refunded' && !r.returnInvoiceId && !r.refundMode && (
                            <Tooltip title={tt('İade talebini reddet', 'Reject return request')}>
                              <IconButton size="small" color="error" onClick={() => openRejectDialog(r)}>
                                <BlockIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.expenseVoucherStatus && (
                            <Tooltip title={tt('Gider pusulası belge girişi', 'Record expense voucher')}>
                              <IconButton
                                size="small"
                                color={r.expenseVoucherStatus === 'pending' ? 'warning' : 'default'}
                                onClick={() => openVoucherDialog(r)}
                              >
                                <DescriptionIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {r.expenseVoucherPdfUrl && (
                            <Tooltip title={tt('Belge PDF', 'Voucher PDF')}>
                              <IconButton size="small" component={Link} href={r.expenseVoucherPdfUrl} target="_blank" rel="noopener">
                                <PictureAsPdfIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredReturns.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
            />
          </>
        )}
      </Paper>

      {/* İadeyi İşle Diyaloğu */}
      <Dialog open={refundDialogOpen} onClose={() => setRefundDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tt('İadeyi İşle', 'Process Refund')} — {refundTarget?.returnNumber}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {tt(
              'İADE faturası kesilir, cari hesaba alacak yazılır ve (stok düşülmüşse) ürünler stoğa geri alınır. Bu işlem idempotenttir; tekrar çalıştırılırsa mevcut kayıt döner.',
              'A return invoice is issued, the account is credited, and (if stock was deducted) items are restocked. This operation is idempotent.'
            )}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>{tt('Para İade Modu', 'Refund Mode')}</InputLabel>
                <Select
                  value={refundMode}
                  label={tt('Para İade Modu', 'Refund Mode')}
                  onChange={(e) => setRefundMode(e.target.value)}
                >
                  <MenuItem value="balance">{tt('Cari bakiyeye alacak (varsayılan)', 'Credit to account balance (default)')}</MenuItem>
                  <MenuItem value="provider">{tt('Sağlayıcı üzerinden geri ödeme (banka çıkışı)', 'Refund via provider (bank withdrawal)')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customerIssuesInvoice}
                    onChange={(e) => setCustomerIssuesInvoice(e.target.checked)}
                  />
                }
                label={tt(
                  'Müşteri e-Fatura mükellefi — iade faturasını kendisi kesecek (sistem İADE faturası kesmez)',
                  'Customer is an e-Invoice taxpayer — they will issue the return invoice themselves (system skips the return invoice)'
                )}
              />
            </Grid>
            {refundMode === 'provider' && (
              <Grid item xs={12}>
                <Alert severity="info">
                  {tt(
                    'Banka çıkışı tahsilat banka hesabından yapılır; ödeme sağlayıcısı tarafındaki iade işlemi manuel olarak tamamlanmalıdır.',
                    'The bank withdrawal is posted from the collection bank account; the provider-side refund must be completed manually.'
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)} disabled={refundProcessing}>
            {tt('Vazgeç', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleProcessRefund}
            disabled={refundProcessing}
            startIcon={refundProcessing ? <CircularProgress size={18} color="inherit" /> : <PlayCircleOutlineIcon />}
          >
            {tt('İşle', 'Process')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İade Reddi Diyaloğu */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tt('İade Talebini Reddet', 'Reject Return Request')} — {rejectTarget?.returnNumber}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {tt(
              'Ret nedeni müşteriye gösterilir. Reddedilen iade işlenemez; müşteri gerekirse yeni bir iade talebi açabilir.',
              'The rejection reason is shown to the customer. A rejected return cannot be processed; the customer may open a new request if needed.'
            )}
          </DialogContentText>
          <TextField
            fullWidth
            required
            multiline
            rows={3}
            size="small"
            label={tt('Ret Nedeni', 'Rejection Reason')}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={rejectSaving}>
            {tt('Vazgeç', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectReturn}
            disabled={rejectSaving || !rejectReason.trim()}
            startIcon={rejectSaving ? <CircularProgress size={18} color="inherit" /> : <BlockIcon />}
          >
            {tt('Reddet', 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Gider Pusulası Diyaloğu */}
      <Dialog open={voucherDialogOpen} onClose={() => setVoucherDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {tt('Gider Pusulası Belge Girişi', 'Expense Voucher Entry')} — {voucherTarget?.returnNumber}
        </DialogTitle>
        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            {tt(
              'e-Arşiv (B2C) iadesinde muhasebe tarafından düzenlenen gider pusulasının belge numarası ve tarihi kaydedilir.',
              'Record the document number and date of the expense voucher issued by accounting for the e-Archive (B2C) return.'
            )}
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                size="small"
                label={tt('Belge No', 'Document No')}
                value={voucherNo}
                onChange={(e) => setVoucherNo(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={tt('Belge Tarihi', 'Document Date')}
                value={voucherDate}
                onChange={(e) => setVoucherDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label={tt('PDF URL (opsiyonel)', 'PDF URL (optional)')}
                value={voucherPdfUrl}
                onChange={(e) => setVoucherPdfUrl(e.target.value)}
                placeholder="https://..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoucherDialogOpen(false)} disabled={voucherSaving}>
            {tt('Vazgeç', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveVoucher}
            disabled={voucherSaving || !voucherNo.trim()}
            startIcon={voucherSaving ? <CircularProgress size={18} color="inherit" /> : <DescriptionIcon />}
          >
            {tt('Kaydet', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReturnsManagement;
